import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, Copy, Ban, Trash2, Link as LinkIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface Invite {
  id: string;
  email: string;
  token: string;
  credits: number;
  expires_at: string | null;
  status: string;
  created_at: string;
  used_at: string | null;
  used_by: string | null;
  revoked_at: string | null;
  isBetaLinked?: boolean;
}

const DEFAULT_CREDITS_KEY = "admin_invite_default_credits";
const DEFAULT_EXPIRY_KEY = "admin_invite_default_expiry";

export const InviteManagement = () => {
  const { toast } = useToast();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [credits, setCredits] = useState<string>(() => localStorage.getItem(DEFAULT_CREDITS_KEY) || "1000");
  const [expiresInDays, setExpiresInDays] = useState<string>(() => localStorage.getItem(DEFAULT_EXPIRY_KEY) || "90");
  const [neverExpires, setNeverExpires] = useState(false);

  const fetchInvites = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("invites")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Error", description: "Failed to load invites", variant: "destructive" });
    } else {
      const loadedInvites = (data || []) as Invite[];
      const inviteIds = loadedInvites.map((invite) => invite.id);
      let betaInviteIds = new Set<string>();

      if (inviteIds.length) {
        const { data: betaApplications, error: betaApplicationsError } = await supabase
          .from("waitlist")
          .select("invite_id")
          .in("invite_id", inviteIds);
        if (betaApplicationsError) {
          toast({
            title: "Invite history unavailable",
            description: "Linked beta invites are protected from deletion until their history can be verified.",
            variant: "destructive",
          });
          // A failed audit lookup must not expose destructive controls for an
          // invite that may be referenced by a retained beta application.
          betaInviteIds = new Set(inviteIds);
        } else {
          betaInviteIds = new Set(
            (betaApplications || [])
              .map((application) => application.invite_id)
              .filter((inviteId): inviteId is string => Boolean(inviteId)),
          );
        }
      }

      setInvites(loadedInvites.map((invite) => ({
        ...invite,
        isBetaLinked: betaInviteIds.has(invite.id),
      })));
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const buildInviteUrl = (invite: Invite) =>
    `${window.location.origin}/auth?invite=${invite.token}&email=${encodeURIComponent(invite.email)}`;

  const computedStatus = (invite: Invite): { label: string; color: string } => {
    if (invite.status === "used" && invite.isBetaLinked) return { label: "Used · beta record", color: "border-blue-500/50 bg-blue-500/10 text-blue-400" };
    if (invite.status === "used") return { label: "Used", color: "border-blue-500/50 bg-blue-500/10 text-blue-400" };
    if (invite.status === "revoked") return { label: "Revoked", color: "border-rose-500/50 bg-rose-500/10 text-rose-400" };
    if (invite.expires_at && new Date(invite.expires_at).getTime() < Date.now()) {
      return { label: "Expired", color: "border-yellow-500/50 bg-yellow-500/10 text-yellow-400" };
    }
    return { label: "Active", color: "border-emerald-500/50 bg-emerald-500/10 text-emerald-400" };
  };

  const handleCreate = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      toast({ title: "Email required", variant: "destructive" });
      return;
    }
    const creditsNum = parseInt(credits, 10);
    if (!Number.isFinite(creditsNum) || creditsNum <= 0) {
      toast({ title: "Invalid credits", description: "Credits must be a positive number", variant: "destructive" });
      return;
    }

    let daysOrNull: number | null = null;
    if (!neverExpires) {
      const d = parseInt(expiresInDays, 10);
      if (!Number.isFinite(d) || d <= 0) {
        toast({ title: "Invalid expiration", description: "Days must be a positive number", variant: "destructive" });
        return;
      }
      daysOrNull = d;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-invite", {
        body: {
          email: trimmedEmail,
          credits: creditsNum,
          expiresInDays: daysOrNull,
        },
      });

      if (error || !data?.success) {
        toast({
          title: "Error",
          description: error?.message || data?.error || "Failed to create invite",
          variant: "destructive",
        });
      } else {
        // Persist defaults so the next invite uses the same numbers
        localStorage.setItem(DEFAULT_CREDITS_KEY, String(creditsNum));
        if (daysOrNull) localStorage.setItem(DEFAULT_EXPIRY_KEY, String(daysOrNull));

        toast({ title: "Invite created", description: trimmedEmail });
        setIsDialogOpen(false);
        setEmail("");
        await fetchInvites();

        // Auto-copy the new link
        const newInvite = data.invite as Invite;
        const url = `${window.location.origin}/auth?invite=${newInvite.token}&email=${encodeURIComponent(newInvite.email)}`;
        try {
          await navigator.clipboard.writeText(url);
          toast({ title: "Link copied", description: "Invite link copied to clipboard" });
        } catch {
          /* ignore */
        }
      }
    } catch (err: unknown) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to create invite",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async (invite: Invite) => {
    try {
      await navigator.clipboard.writeText(buildInviteUrl(invite));
      toast({ title: "Copied", description: "Invite link copied to clipboard" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const handleRevoke = async (invite: Invite) => {
    if (!confirm(`Revoke invite for ${invite.email}? This link will stop working.`)) return;
    const { error } = await supabase
      .from("invites")
      .update({ status: "revoked", revoked_at: new Date().toISOString() })
      .eq("id", invite.id);
    if (error) {
      toast({ title: "Error", description: "Failed to revoke", variant: "destructive" });
    } else {
      toast({ title: "Revoked", description: invite.email });
      fetchInvites();
    }
  };

  const handleDelete = async (invite: Invite) => {
    if (!confirm(`Delete invite for ${invite.email}?`)) return;
    const { error } = await supabase.from("invites").delete().eq("id", invite.id);
    if (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    } else {
      toast({ title: "Deleted" });
      fetchInvites();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Invites</h2>
          <p className="text-muted-foreground">Create and manage private invite links</p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />Create Invite
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : invites.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            No invites yet. Create the first one to onboard a user.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {invites.map((invite) => {
            const status = computedStatus(invite);
            const isActive = status.label === "Active";
            const isDeletionProtected = Boolean(invite.isBetaLinked);
            return (
              <Card key={invite.id} className="border-border/50 bg-card/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-foreground truncate">{invite.email}</h3>
                        <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", status.color)}>
                          {status.label}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-4 gap-y-1">
                        <span>{invite.credits} credits</span>
                        <span>
                          {invite.expires_at
                            ? `Expires ${new Date(invite.expires_at).toLocaleDateString()}`
                            : "No expiration"}
                        </span>
                        <span>Created {new Date(invite.created_at).toLocaleDateString()}</span>
                        {invite.used_at && (
                          <span>Used {new Date(invite.used_at).toLocaleDateString()}</span>
                        )}
                        {invite.isBetaLinked && (
                          <span>Retained with its beta application record</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(invite)}
                        disabled={!isActive}
                        title="Copy invite link"
                      >
                        <Copy className="h-4 w-4 mr-1" />Copy link
                      </Button>
                      {isActive && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRevoke(invite)}
                          title="Revoke invite"
                        >
                          <Ban className="h-4 w-4" />
                        </Button>
                      )}
                      {!isDeletionProtected && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(invite)}
                          title="Delete invite"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Invite</DialogTitle>
            <DialogDescription>
              Generate a one-time invite link tied to a specific email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="invite-credits">Credits</Label>
              <Input
                id="invite-credits"
                type="number"
                min="1"
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Saved as the new default for the next invite.
              </p>
            </div>
            <div>
              <Label htmlFor="invite-expiry">Expires in (days)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="invite-expiry"
                  type="number"
                  min="1"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                  disabled={neverExpires}
                />
              </div>
              <label className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={neverExpires}
                  onChange={(e) => setNeverExpires(e.target.checked)}
                />
                No expiration
              </label>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={submitting}>
                <LinkIcon className="h-4 w-4 mr-2" />
                {submitting ? "Creating..." : "Create & Copy Link"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
